import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const Register = () => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '', country: '' });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/profile';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name, formData.country);
      toast.success('Welcome to the sisterhood');
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-ivory" data-testid="register-page">
      <div className="max-w-md w-full space-y-10">
        <div className="text-center space-y-4">
          <p className="label-editorial">Join Behenchara</p>
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-tight" data-testid="register-title">
            Begin your <em className="text-burgundy not-italic">sisterhood</em>.
          </h1>
        </div>

        <button
          onClick={handleGoogleSignup}
          className="w-full py-4 border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory transition-colors font-medium text-sm uppercase tracking-widest flex items-center justify-center gap-3"
          data-testid="google-signup-button"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4">
          <div className="flex-1 hairline"></div>
          <span className="text-xs text-muted-warm font-mono uppercase tracking-widest">or</span>
          <div className="flex-1 hairline"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
          <div>
            <label htmlFor="name" className="label-editorial block mb-3">Full Name</label>
            <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} required
              className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
              data-testid="name-input" />
          </div>
          <div>
            <label htmlFor="email" className="label-editorial block mb-3">Email</label>
            <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required
              className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
              data-testid="email-input" />
          </div>
          <div>
            <label htmlFor="country" className="label-editorial block mb-3">Country (Optional)</label>
            <input id="country" name="country" type="text" value={formData.country} onChange={handleChange}
              className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
              data-testid="country-input" />
          </div>
          <div>
            <label htmlFor="password" className="label-editorial block mb-3">Password</label>
            <input id="password" name="password" type="password" value={formData.password} onChange={handleChange} required
              className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
              data-testid="password-input" />
          </div>
          <div>
            <label htmlFor="confirmPassword" className="label-editorial block mb-3">Confirm Password</label>
            <input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} required
              className="w-full py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
              data-testid="confirm-password-input" />
          </div>
          <button type="submit" disabled={loading}
            className="btn-burgundy w-full py-4 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="register-submit-button">
            {loading ? 'Creating' : 'Create Account'} <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        <p className="text-center text-charcoal-muted text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-burgundy hover:underline font-medium" data-testid="login-link">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
